import { action, observable, computed } from 'mobx';
import { WebGivingPage } from '../index';
import { validateFields, ValidationState } from '../../validation/view-model-validator';
import { WebGivingMerchant, CardBrandConfig, PaymentMethodType, MerchantCardPreference,
	WebGivingPersonModel, WebGivingAddPaymentMethodResultCode } from '../../webgiving-generated';
import { getCardBrandsFromKeys } from '../../utils/card-utils';
import { CardPaymentMethodViewModel } from './card-payment-method-view-model';
import { ITabOption } from '../../components/tabs/tabs';
import { AchPaymentMethodViewModel } from './ach-payment-method-view-model';
import cardIcon from '../../assets/svg/icon-card.svg';
import achIcon from '../../assets/svg/checkbook.svg';
import { WebGivingPageViewModelBase } from '../page-view-model-base';
import { WebGivingErrorType, WebGivingError } from '../../components/error-message/error-message';

export class PagePaymentMethodViewModel extends WebGivingPageViewModelBase {
	readonly type: WebGivingPage.PaymentMethod = WebGivingPage.PaymentMethod;

	// mobx observable decorator removed as it caused issues when running our front end test suites
	// it has been replaced with an observable syntax which achieves similar behavior
	paymentMethods: ITabOption[] = observable([]);

	@computed
	get paymentMethodViewModel() : AchPaymentMethodViewModel | CardPaymentMethodViewModel {
		return this.paymentMethodType === PaymentMethodType.CreditCard ? this.cardModel : this.achModel;
	}

	@computed
	get merchantSupportsCard() : boolean {
		return this.merchant.SupportedCardBrandKeys && this.merchant.SupportedCardBrandKeys.length > 0;
	}

	@observable
	paymentMethodType: PaymentMethodType;

	@observable
	validationState?: ValidationState;

	@observable
	cardModel: CardPaymentMethodViewModel;

	supportedCardBrands: CardBrandConfig[];

	@observable
	achModel: AchPaymentMethodViewModel;

	showBackToConfirmGift: boolean;

	private errorsByTab: { [key:number]:WebGivingError};

	@action
	setError(error: WebGivingError): void {

		if(error && error.errorType === WebGivingErrorType.AddPaymentMethodError && error.errorResultCode === WebGivingAddPaymentMethodResultCode.CardTokenizationUnavailable) {
			this.createPaymentMethodTabs({cardTokenizationUnavailable: true});
		}

		this.error = error;
	}

	@action
	validate(): ValidationState {
		return validateFields(this.paymentMethodViewModel, 'Please enter valid details');
	}

	constructor(
		public merchant: WebGivingMerchant,
		person: WebGivingPersonModel,
		public hideACH = false,
		paymentMethodType?: PaymentMethodType,
		achModel?: AchPaymentMethodViewModel,
	) {
		super();

		this.achModel = achModel || new AchPaymentMethodViewModel();
		this.cardModel = new CardPaymentMethodViewModel(merchant, person.PostalCodeCommonName, person.PostalCodeRegex);
		this.supportedCardBrands = getCardBrandsFromKeys(this.merchant.SupportedCardBrandKeys);
		this.paymentMethodType = paymentMethodType || merchant.PaymentMethodPreference;
		this.errorsByTab = {};
		this.createPaymentMethodTabs();

		if (NewFeatures.SunsetFastpay && hideACH) {
			const nonACHMethod = this.paymentMethods.filter(c=> c.value !== PaymentMethodType.ACH)[0];
			if (nonACHMethod) this.paymentMethodType = nonACHMethod.value;
		}

		// ensure there's a payment method selected
		if (!this.paymentMethodType) {
			this.paymentMethodType = this.paymentMethods[0].value;
		}

		this.showBackToConfirmGift = person.PaymentMethods && person.PaymentMethods.length > 0;
	}

	@action.bound
	switchPaymentMethodType (value: number) {
		if (this.paymentMethodType !== value) {
			this.validationState = undefined;
			this.paymentMethodViewModel.clearValidation();
		}

		if(this.error) {
			this.errorsByTab[this.paymentMethodType] = this.error;
		}
		this.error = this.errorsByTab[value];

		this.paymentMethodType = value;
	}

	createPaymentMethodTabs (options?: {cardTokenizationUnavailable: boolean}) {
		this.paymentMethods = [];
		const cardTabAvailabilityLabel = options && options.cardTokenizationUnavailable
			? ' (Not available)'
			: '';

		const cardTabLabel = this.merchant.CardPreference === MerchantCardPreference.AnyCard
			? 'Card' + cardTabAvailabilityLabel
			: 'Debit card' + cardTabAvailabilityLabel;

		if (this.merchantSupportsCard) {
			this.paymentMethods.push({
				label: cardTabLabel,
				value: PaymentMethodType.CreditCard,
				icon: cardIcon,
				acceptanceTestTargetId: 'tab-payment-method-card',
			});
		}

		if (this.merchant.SupportsAch) {
			this.paymentMethods.push({
				label: 'Bank account',
				value: PaymentMethodType.ACH,
				icon: achIcon,
				acceptanceTestTargetId: 'tab-payment-method-ach',
			});
		}
	}
}

